import { DomainModelRole } from '../constants/cardTypes.js'
import { SchemaType } from '../constants/schema.js'
import { sanitizeCardDescription } from '../utils/sanitize.js'
import { groupAndMergeReadModels } from './utils.js'

export const findEntitiesInWorkflow = (workflow, boundedContextId) => {
  const { schemas, domainEvents } = workflow || {}

  if (!schemas || !boundedContextId) {
    return []
  }

  const entitySchemas = schemas.filter((schema) => schema.type === SchemaType.ENTITY)
  const entitiesById = Object.fromEntries(entitySchemas.map((entity) => [entity.id, entity]))

  const initialIds = entitySchemas
    .filter((entity) => entity.boundedContext === boundedContextId)
    .map((entity) => entity.id)

  const commandRelatedIds = []
  const readModelRelatedIds = []
  const readModelEntityIds = []
  const entityIdsSet = new Set(initialIds)

  for (const event of domainEvents || []) {
    const entityCard = (event.cards ?? []).find((rt) => rt.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
    if (entityCard && entityIdsSet.has(entityCard.schemaId)) {
      const commandCard = event.cards?.find((rt) => rt.cardType?.domainModelRole === DomainModelRole.COMMAND)
      if (commandCard) {
        const commandSchema = commandCard.schemaId
          ? schemas.find((s) => s.id === commandCard.schemaId)
          : null
        const relatedIds = (commandSchema?.fields || [])
          .map((field) => field.relatedEntityId)
          .filter(Boolean)
        commandRelatedIds.push(...relatedIds)
      }

      const readModelCards = event.cards?.filter((rt) => rt.cardType?.domainModelRole === DomainModelRole.READ_MODEL) || []
      for (const readModelCard of readModelCards) {
        const readModelSchema = readModelCard.schemaId
          ? schemas.find((s) => s.id === readModelCard.schemaId)
          : null
        const readModelEntityId = readModelSchema?.entityId
        if (!readModelEntityId) {
          continue
        }

        readModelEntityIds.push(readModelEntityId)

        const relatedIds = (readModelSchema?.fields || [])
          .map((field) => field.relatedEntityId)
          .filter(Boolean)
        readModelRelatedIds.push(...relatedIds)
      }
    }
  }

  const entityIds = new Set([
    ...initialIds,
    ...commandRelatedIds,
    ...readModelRelatedIds,
    ...readModelEntityIds
  ])

  for (const id of entityIds) {
    const entity = entitiesById[id]
    if (!entity) {
      continue
    }

    const relatedIds = (entity.fields || [])
      .map((field) => field.relatedEntityId)
      .filter(Boolean)

    for (const relatedId of relatedIds) {
      if (entitiesById[relatedId]) {
        entityIds.add(relatedId)
      }
    }
  }

  return Array.from(entityIds).map((id) => {
    const entity = entitiesById[id]

    return {
      id: entity.id,
      name: entity.name,
      fields: (entity.fields || []).map(({
        name,
        exampleData,
        cardinality,
        relatedEntityId,
        tags,
        dataType
      }) => ({
        name,
        exampleData,
        cardinality,
        tags,
        relatedEntityId: relatedEntityId ?? undefined,
        dataType: dataType ?? 'string'
      }))
    }
  })
}

export const findCommandsInWorkflow = (workflow, boundedContextId) => {
  if (!workflow) {
    return []
  }

  const contextEntities = workflow.schemas || []
  const entityIds = new Set(contextEntities.filter((e) => e.boundedContext === boundedContextId).map((e) => e.id))

  const commands = []
  for (const event of workflow.domainEvents || []) {
    const entityCard = (event.cards ?? []).find((rt) => rt.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
    if (entityCard && entityIds.has(entityCard.schemaId)) {
      const commandCard = event.cards?.find((rt) => rt.cardType?.domainModelRole === DomainModelRole.COMMAND)
      if (commandCard) {
        const commandSchema = commandCard.schemaId
          ? (workflow.schemas || []).find((s) => s.id === commandCard.schemaId)
          : null
        const gwtCards = (event.cards || []).filter((rt) => rt.cardType?.domainModelRole === DomainModelRole.GIVEN_WHEN_THEN)
        const gwtDescriptions = gwtCards.map((card) => sanitizeCardDescription(card.description || ''))
        commands.push({
          id: commandCard.id,
          description: sanitizeCardDescription(commandCard.description || ''),
          fields: commandSchema?.fields.map((field) => ({
            name: field.name,
            type: field.type ?? undefined,
            relatedEntityId: field.relatedEntityId ?? undefined
          })) || [],
          gwtDescriptions: commandCard.gwtDescriptions || gwtDescriptions
        })
      }
    }
  }

  return commands
}

export const findReadModelsInWorkflow = (workflow, boundedContextId, entities) => {
  if (!workflow || !entities) {
    return []
  }
  const entityIds = new Set(entities.map((e) => e.id))

  const allReadModels = []
  for (const event of workflow.domainEvents || []) {
    const entityCard = (event.cards ?? []).find((rt) => rt.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
    if (entityCard && entityIds.has(entityCard.schemaId)) {
      const readModelCards = event.cards?.filter((rt) => rt.cardType?.domainModelRole === DomainModelRole.READ_MODEL) || []
      for (const readModelCard of readModelCards) {
        const readModelSchema = readModelCard.schemaId
          ? (workflow.schemas || []).find((s) => s.id === readModelCard.schemaId)
          : null
        const readModelEntityId = readModelSchema?.entityId
        if (!readModelEntityId || !entityIds.has(readModelEntityId)) {
          continue
        }

        allReadModels.push({
          id: readModelCard.id,
          description: sanitizeCardDescription(readModelCard.description || ''),
          cardinality: readModelSchema?.cardinality,
          entityId: readModelEntityId,
          fields: readModelSchema?.fields.map((field) => ({
            name: field.name,
            type: field.type ?? undefined,
            relatedEntityId: field.relatedEntityId ?? undefined
          })) || []
        })
      }
    }
  }

  const mergedReadModels = groupAndMergeReadModels(allReadModels, entities)

  return mergedReadModels.map((rm) => ({
    id: rm.id,
    description: rm.description,
    cardinality: rm.cardinality,
    entityId: rm.entityId,
    fields: rm.fields,
    allIds: rm.allIds,
    allDescriptions: rm.allDescriptions
  }))
}

export const findGWTsInWorkflow = (workflow, boundedContextId) => {
  if (!workflow) {
    return []
  }

  const contextEntities = workflow.schemas || []
  const entityIds = new Set(contextEntities.filter((e) => e.boundedContext === boundedContextId).map((e) => e.id))

  const gwts = []
  for (const event of workflow.domainEvents || []) {
    const entityCard = (event.cards ?? []).find((rt) => rt.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
    if (entityCard && entityIds.has(entityCard.schemaId)) {
      const gwtCards = event.cards?.filter((rt) => rt.cardType?.domainModelRole === DomainModelRole.GIVEN_WHEN_THEN) || []
      for (const gwtCard of gwtCards) {
        gwts.push({
          id: gwtCard.id,
          description: sanitizeCardDescription(gwtCard.description || '')
        })
      }
    }
  }

  return gwts
}
